<template>
	<div class="pet-card" v-if="pet" :style="{ margin }">
		<template>
			<uiCardLayout class="top">
				<template #left>
					<div v-if="pet.avatar" slot="left" class="pr-3">
						<uiImage
							:url="pet.avatar"
							w="60"
							radius-class="rounded-full"
						/>
					</div>
				</template>

				<div slot="center">
					<h3>{{ pet.name }}</h3>
					<p>{{ pet.breed.name }}</p>
					<p class="global-id">{{ pet.id }}</p>
				</div>
			</uiCardLayout>
			<uiDevider margin="5px 0" />

			<uiButton color="grey" style="margin: 10px 0" @click="startEditing">
				Редактировать
			</uiButton>

			<template v-if="!editing">
				<p>{{ pet.kind === 'dog' ? 'Собака' : 'Кошка' }}</p>
				<p>{{ gender }}</p>
				<p>{{ age }}</p>
				<p>{{ pet.weight }} кг</p>
				<p>Cтерилизация: {{ pet.sterilized ? 'Да' : 'нет' }}</p>
				<p>Чип: {{ pet.chip || 'нет' }}</p>
				<p>Клеймо: {{ pet.stamp || 'нет' }}</p>
				<p>Живет: {{ pet.home || 'не знаем' }}</p>
				<p>Питание: {{ pet.food || 'не знаем' }}</p>

				<uiFold
					title="Особенности здоровья"
					v-if="pet.specialities.length"
					:openedAtMount="true"
				>
					<div v-for="s in pet.specialities" :key="s.id">
						<div style="font-weight: bold">{{ s.title }}</div>
						<div style="font-size: 14px; opacity: 0.5">
							{{ s.comment }}
						</div>
					</div>
				</uiFold>
			</template>

			<template v-else>
				<uiInput
					v-model="pet.name"
					:label="true"
					placeholder="Кличка"
					style="margin: 5px 0"
				/>
				<uiDropdown
					:value="pet.gender === 'male' ? 'Мальчик' : 'Девочка'"
					@input="onPetGender"
					label="Пол"
					style="margin: 5px 0"
					:options="[
						{ value: 'male', label: 'Мальчик' },
						{ value: 'female', label: 'Девочка' },
					]"
				/>

				<uiDropdown
					v-model="pet.breed.id"
					label="Порода"
					:options="breeds"
					:reduce="item => item.value"
					style="margin: 5px 0"
					clearSearchOnSelect
				/>

				<uiDateInput
					v-model="pet.birth"
					label="Дата рождения"
					style="margin-top: 5px"
				/>

				<p style="margin-bottom: 10px; font-size: 15px">
					Возраст: {{ editedAge(pet.birth) }}
				</p>

				<uiInput
					v-model="pet.weight"
					label="Вес в кг"
					mask="9{1,2}[.]9{1,2}"
					style="margin: 5px 0"
				/>
				<uiInput
					v-model="pet.chip"
					label="Номера чипа"
					style="margin: 5px 0"
				/>
				<uiInput
					v-model="pet.stamp"
					label="Клеймо"
					style="margin: 5px 0"
				/>

				<div style="display: flex; align-items: center; margin: 15px 0">
					<div style="width: 100%; flex: 1">Стерилизация</div>
					<uiSwitch style="width: auto" v-model="pet.sterilized" />
				</div>

				<uiDateInput
					v-model="lastComplexVaccinationValue"
					label="Прививка (комплекс)"
					style="margin: 5px 0"
				/>

				<uiDateInput
					v-model="lastRabiesVaccinationValue"
					label="Прививка (бешенство)"
					style="margin: 5px 0"
				/>

				<uiDateInput
					v-model="lastParasiteTreatmentValue"
					label="Глистогонка"
					style="margin: 5px 0"
				/>

				<uiDateInput
					v-model="lastAcariTreatmentValue"
					label="От клещей"
					style="margin: 5px 0"
				/>

				<uiButton
					@click="save"
					:loading="loading"
					style="margin-top: 20px"
				>
					Сохранить
				</uiButton>

				<div class="cancel" @click="editing = false">Отмена</div>

				<uiButton
					@click="remove"
					:loading="loading"
					v-if="$isGod()"
					color="light"
					style="margin-top: 20px"
				>
					Удалить
				</uiButton>
			</template>

			<uiDevider />

			<uiFold title="Владельцы" :small="true">
				<router-link
					:to="'/users/' + owner.id"
					class="row"
					style="border: none"
					v-for="owner in pet.owners"
					:key="owner.id"
				>
					{{ owner.name }} {{ owner.surname }}
				</router-link>
			</uiFold>
		</template>
	</div>
</template>

<script>
import Format from '@/utils/format'
import { API } from '@/config'
import { differenceInMonths, format, addDays } from 'date-fns'

export default {
	name: 'PetCard',
	props: {
		id: String,
		margin: {
			type: String,
			default: '10px',
		},
	},
	data() {
		return {
			loading: false,
			loadingEvents: false,
			editing: false,
			pet: null,
			events: [],
			noMoreEvents: false,
			breeds: [],
		}
	},
	computed: {
		gender() {
			if (this.pet.gender === 'male') return 'Мальчик'
			return 'Девочка'
		},
		age() {
			return Format.dateToAge(this.pet.birth)
		},
		lastComplexVaccinationValue: {
			get() {
				return this.importantDateGetter({ kind: 'complexVaccination' })
			},
			set(val) {
				return this.importantDateSetter({
					kind: 'complexVaccination',
					val: val,
					period: 365,
				})
			},
		},
		lastComplexVaccination() {
			if (!this.lastComplexVaccinationValue) return
			let date = new Date(this.lastComplexVaccinationValue)
			let diff = differenceInMonths(new Date(), date)
			let formatted = format(date, 'dd.MM.yyyy')
			if (diff === 0) return formatted + ' (этот месяц)'
			return formatted + ' (' + diff + ' мес назад)'
		},
		lastRabiesVaccinationValue: {
			get() {
				return this.importantDateGetter({ kind: 'rabiesVaccination' })
			},
			set(val) {
				return this.importantDateSetter({
					kind: 'rabiesVaccination',
					val: val,
					period: 365,
				})
			},
		},
		lastRabiesVaccination() {
			if (!this.lastRabiesVaccinationValue) return
			let date = new Date(this.lastRabiesVaccinationValue)
			let diff = differenceInMonths(new Date(), date)
			let formatted = format(date, 'dd.MM.yyyy')
			if (diff === 0) return formatted + ' (этот месяц)'
			return formatted + ' (' + diff + ' мес назад)'
		},
		lastParasiteTreatmentValue: {
			get() {
				return this.importantDateGetter({ kind: 'parasiteTreatment' })
			},
			set(val) {
				return this.importantDateSetter({
					kind: 'parasiteTreatment',
					val: val,
					period: 30,
				})
			},
		},
		lastParasiteTreatment() {
			if (!this.lastParasiteTreatmentValue) return
			let date = new Date(this.lastParasiteTreatmentValue)
			let diff = differenceInMonths(new Date(), date)
			let formatted = format(date, 'dd.MM.yyyy')
			if (diff === 0) return formatted + ' (этот месяц)'
			return formatted + ' (' + diff + ' мес назад)'
		},
		lastAcariTreatmentValue: {
			get() {
				return this.importantDateGetter({ kind: 'acariTreatment' })
			},
			set(val) {
				return this.importantDateSetter({
					kind: 'acariTreatment',
					val: val,
					period: 30,
				})
			},
		},
		lastAcariTreatment() {
			if (!this.lastAcariTreatmentValue) return
			let date = new Date(this.lastAcariTreatmentValue)
			let diff = differenceInMonths(new Date(), date)
			let formatted = format(date, 'dd.MM.yyyy')
			if (diff === 0) return formatted + ' (этот месяц)'
			return formatted + ' (' + diff + ' мес назад)'
		},
		acariTreatmentType() {
			if (!this.pet.acariTreatmentType) return
			if (this.pet.acariTreatmentType === 'pill') return 'Таблетки'
			return 'Капли'
		},
		parasiteTreatmentPeriod() {
			if (!this.pet.parasiteTreatmentPeriod) return 'Не знаем'
			return 'Раз в ' + this.pet.parasiteTreatmentPeriod + ' нед'
		},
		acariTreatmentPeriod() {
			if (!this.pet.acariTreatmentPeriod) return 'Не знаем'
			return 'Раз в ' + this.pet.acariTreatmentPeriod + ' нед'
		},

		valid() {
			return (
				this.editing &&
				this.pet.name.length &&
				this.pet.birth &&
				this.pet.weight
			)
		},
	},
	watch: {
		async id(nv, pv) {
			if (nv !== pv) {
				this.setPet()
				this.editing = false
				this.events = []
				this.noMoreEvents = false
				await this.$nextTick()
			}
		},
		async editing(val) {
			if (!val) return
			await this.getBreeds()
		},
	},
	async created() {
		this.setPet()
		await this.$nextTick()
	},
	methods: {
		importantDateGetter({ kind } = {}) {
			const vaccination = this.pet.importantDates.find(
				d => d.kind === kind
			)
			return vaccination?.last ?? ''
		},
		importantDateSetter({ kind, val, period } = {}) {
			const vaccination = this.pet.importantDates.find(
				d => d.kind === kind
			)
			const formatStartDay = value =>
				format(value, "yyyy-MM-dd'T'00:00:00.000'Z'")
			const last = formatStartDay(val)

			if (vaccination?.last) {
				vaccination.last = last
				vaccination.next = formatStartDay(
					addDays(new Date(last), vaccination.period ?? period)
				)
			} else {
				this.pet.importantDates.push({
					kind: kind,
					period: period,
					last: last,
					next: formatStartDay(addDays(new Date(last), period)),
				})
			}
		},
		setPet() {
			this.pet = JSON.parse(
				JSON.stringify(
					this.$store.getters.itemById({ path: 'pets', id: this.id })
				)
			)
		},
		startEditing() {
			this.setPet()
			this.editing = true
		},
		onPetGender(e) {
			if (!this.pet) return
			this.pet.gender = e.value
		},
		async save() {
			if (!this.valid) return
			if (this.loading) return
			this.loading = true

			let payload = structuredClone({
				...this.pet,
				breed: this.pet.breed.id,
			})
			let { data } = await this.$http.post(API.PETS.update, payload)
			if (!data || data.err) {
				this.$notify(data.err)
				return (this.loading = false)
			}

			this.$store.commit('updateInArray', { path: 'pets', data: data })
			this.loading = false
			this.editing = false
		},
		editedAge(date) {
			if (!this.editing) return
			return Format.dateToAge(date)
		},
		async remove() {
			let confirm = await this.$confirm('Точно удаляем?')
			if (!confirm) return

			let confirm2 = await this.$confirm('Подумай еще раз')
			if (!confirm2) return

			if (this.loading) return
			this.loading = true

			let { data } = await this.$http.post(API.PETS.remove, {
				id: this.id,
			})
			if (!data || data.err) this.$notify('Не могу удалить питомца')
			this.loading = false
			this.$emit('remove', this.id)
		},
		async getBreeds() {
			const { data } = await this.$http.get(API.SERVICES.petBreeds)
			if (!data || data.err) return this.$notify(data.err)

			this.breeds = data
				.filter(breed => breed.kind === this.pet.kind)
				.map(breed => ({ value: breed.id, label: breed.name }))
		},
	},
}
</script>

<style lang="less" scoped>
@import '../assets/app.less';

h3 {
	margin: 0;
}

p {
	margin: 0;
	opacity: 0.5;
}

.flex-cols {
	display: flex;
}

.row {
	display: block;
	padding-top: 8px;
	margin-bottom: 8px;
	border-top: 2px solid rgba(0, 0, 0, 0.1);

	span {
		display: block;
		opacity: 0.5;
		font-size: 13px;
	}
}

.cancel {
	width: 100%;
	height: 50px;
	line-height: 50px;
	text-align: center;
	cursor: pointer;
}
</style>
